/**
 * Program Intelligence Catalog Component
 * Pre-active marketplace for discovered programs from public data
 * CBOs can browse and express interest in programs
 * Trust Marketplace - Two-way Interest Matching System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Progress } from '../ui/progress';
import { getWeaveinApiUrl } from '@/src/config';
import ProgramMapView from './ProgramMapView';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Search,
  Building2,
  MapPin,
  DollarSign,
  TrendingUp,
  Calendar,
  Filter,
  Lightbulb,
  Users,
  Target,
  Send,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  Star,
  Globe,
  FileText,
  Briefcase,
  Flag,
  Shield,
  Activity,
  ExternalLink,
  List,
  Map
} from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

interface ProgramIntelligence {
  id: number;
  program_name: string;
  discovered_sponsor_name: string;
  discovered_sponsor_type: string;
  sponsor_headquarters_state: string;
  program_type: string;
  description: string;
  data_source: string;

  // Enhanced fields from CMS/external connectors
  program_type_code: string;
  risk_arrangement: string;
  eligibility_criteria: {
    insurance_types?: string[];
    age_min?: number;
    enrollment_required?: boolean;
    attribution_method?: string;
    documentation?: string;
  };
  quality_performance: {
    quality_score?: number;
    per_capita_expenditure?: number;
  };
  financial_performance: {
    savings_rate?: number;
    generated_savings_losses?: number;
    benchmark_expenditure?: number;
  };

  // Geographic
  service_states: string[];
  service_counties: string[];
  coverage_region: string;

  // Timeline
  program_start_date: string;
  program_end_date: string;
  application_deadline: string;

  // Capacity
  target_enrollment: number;
  number_of_providers_needed: number;

  // Requirements
  required_capabilities: any;
  required_certifications: string[];
  required_languages: string[];

  // Services
  covered_services: string[];
  service_categories: string[];
  sdoh_focus_areas: string[];
  target_populations: string[];

  // Reimbursement
  reimbursement_model: string;
  base_reimbursement_rate: number;
  incentive_structure: any;
  total_program_budget: number;
  estimated_per_member_value: number;
  funding_source: string;

  // Status
  intelligence_status: string;
  confidence_score: number;
  current_phase: string;
  rfp_status: string;

  // Scale metrics
  total_interested_cbos: number;
  high_interest_cbos: number;
  total_declared_capacity: number;
  execution_readiness_score: number;

  // Enhanced metadata from CHHS/structured APIs
  enrollment_growth_rate_12mo?: number;
  enrollment_data_points?: number;
  enrollment_trend_direction?: 'growing' | 'declining' | 'stable';
  distance_miles?: number;
  closest_county?: string;
  priority_specialties_needed?: string[];
  current_specialty_counts?: Record<string, number>;

  // Timestamps
  discovered_at: string;
  application_deadline: string;

  // Source
  source_url: string;
  data_source: string;
}

interface ProgramIntelligenceCatalogProps {
  cboId: number;
}

const ProgramIntelligenceCatalog: React.FC<ProgramIntelligenceCatalogProps> = ({ cboId }) => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const { fmt } = useFormattedDate();
  const [programs, setPrograms] = useState<ProgramIntelligence[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<ProgramIntelligence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [sponsorTypeFilter, setSponsorTypeFilter] = useState<string>('');
  const [programTypeFilter, setProgramTypeFilter] = useState<string>('');
  const [stateFilter, setStateFilter] = useState<string>('');
  const [hasDeadlineOnly, setHasDeadlineOnly] = useState(false);

  // Location filter for geocoding
  const [userLocation, setUserLocation] = useState<string>('');
  const [debouncedLocation, setDebouncedLocation] = useState<string>('');
  const [maxDistance, setMaxDistance] = useState<number>(50); // miles

  // View mode: 'list' or 'map'
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  // Expanded cards
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  // Quick favorites (bookmarks)
  const [favoritedPrograms, setFavoritedPrograms] = useState<Set<number>>(new Set());

  // Interest modal
  const [selectedProgram, setSelectedProgram] = useState<ProgramIntelligence | null>(null);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [interestLevel, setInterestLevel] = useState<string>('HIGH');
  const [interestType, setInterestType] = useState<string>('PARTICIPATE');
  const [declaredCapacity, setDeclaredCapacity] = useState<number>(0);
  const [interestNotes, setInterestNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Sponsor types for filter
  const sponsorTypes = [
    { code: 'HEALTH_PLAN', name: 'Health Plan' },
    { code: 'MANAGED_CARE', name: 'Managed Care' },
    { code: 'STATE_AUTHORITY', name: 'State Authority' },
    { code: 'STATE_ASSOCIATION', name: 'State Association' },
    { code: 'FEDERAL_AGENCY', name: 'Federal Agency' },
    { code: 'ACO', name: 'ACO' },
    { code: 'HOSPITAL_SYSTEM', name: 'Hospital System' }
  ];

  // Program types for filter
  const programTypes = [
    'CalAIM_ECM',
    'CalAIM_CS',
    'Housing',
    'Food',
    'Transportation',
    'Medicaid_Enrollment',
    'Preventive_Care',
    'Behavioral_Health'
  ];

  // Debounce userLocation input (wait 800ms after user stops typing)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedLocation(userLocation);
    }, 800);

    return () => clearTimeout(timer);
  }, [userLocation]);

  useEffect(() => {
    fetchPrograms();
  }, [cboId, debouncedLocation, maxDistance]);

  useEffect(() => {
    applyFilters();
  }, [programs, searchTerm, sponsorTypeFilter, programTypeFilter, stateFilter, hasDeadlineOnly, debouncedLocation, maxDistance]);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('limit', '200'); // Request all programs (max 200)
      if (hasDeadlineOnly) params.append('has_deadline', 'true');
      if (debouncedLocation) {
        params.append('user_location', debouncedLocation);
        if (maxDistance) params.append('max_distance', maxDistance.toString());
      }

      const url = getWeaveinApiUrl(`/api/program-intelligence?${params}`);
      const token = localStorage.getItem('auth_token');

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId.toString()
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch programs: ${response.status}`);
      }

      const data = await response.json();
      setPrograms(data || []);
      setFilteredPrograms(data || []);
    } catch (err) {
      console.error('Error fetching program intelligence:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch programs');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...programs];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.program_name?.toLowerCase().includes(term) ||
        p.discovered_sponsor_name?.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term)
      );
    }

    // Sponsor type filter
    if (sponsorTypeFilter) {
      filtered = filtered.filter(p => p.discovered_sponsor_type === sponsorTypeFilter);
    }

    // Program type filter
    if (programTypeFilter) {
      filtered = filtered.filter(p => p.program_type === programTypeFilter);
    }

    // State filter
    if (stateFilter) {
      filtered = filtered.filter(p =>
        p.service_states?.includes(stateFilter) ||
        p.sponsor_headquarters_state === stateFilter
      );
    }

    // Deadline filter
    if (hasDeadlineOnly) {
      const today = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(p =>
        p.application_deadline && p.application_deadline >= today
      );
    }

    // Location/distance filter
    if (userLocation && maxDistance) {
      filtered = filtered.filter(p => {
        // Filter by distance_miles if available from backend geocoding
        if (p.distance_miles !== undefined && p.distance_miles !== null) {
          return p.distance_miles <= maxDistance;
        }
        // If no distance data, include all programs (backend will need location geocoding)
        return true;
      });
    }

    setFilteredPrograms(filtered);
  };

  const toggleCardExpansion = (programId: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(programId)) {
        newSet.delete(programId);
      } else {
        newSet.add(programId);
      }
      return newSet;
    });
  };

  const handleExpressInterest = (program: ProgramIntelligence) => {
    setSelectedProgram(program);
    setShowInterestModal(true);
    setInterestLevel('HIGH');
    setInterestType('PARTICIPATE');
    setDeclaredCapacity(0);
    setInterestNotes('');
  };

  // Quick favorite/bookmark toggle - one-click action without modal
  const toggleFavorite = async (program: ProgramIntelligence) => {
    const isFavorited = favoritedPrograms.has(program.id);

    try {
      const url = getWeaveinApiUrl(`/api/program-intelligence/${program.id}/favorite`);
      const token = localStorage.getItem('auth_token');

      const response = await fetch(url, {
        method: isFavorited ? 'DELETE' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CBO-ID': cboId.toString()
        }
      });

      if (response.ok) {
        // Update local state
        setFavoritedPrograms(prev => {
          const newSet = new Set(prev);
          if (isFavorited) {
            newSet.delete(program.id);
          } else {
            newSet.add(program.id);
          }
          return newSet;
        });
      } else {
        console.error('Failed to toggle favorite');
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      // Still update local state for better UX
      setFavoritedPrograms(prev => {
        const newSet = new Set(prev);
        if (isFavorited) {
          newSet.delete(program.id);
        } else {
          newSet.add(program.id);
        }
        return newSet;
      });
    }
  };

  const submitInterest = async () => {
    if (!selectedProgram) return;

    try {
      setSubmitting(true);
      const url = getWeaveinApiUrl(`/api/program-intelligence/${selectedProgram.id}/express-interest`);
      const token = localStorage.getItem('auth_token');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CBO-ID': cboId.toString()
        },
        body: JSON.stringify({
          interest_level: interestLevel,
          interest_type: interestType,
          declared_capacity: declaredCapacity || null,
          interest_notes: interestNotes || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to express interest');
      }

      setShowInterestModal(false);
      setSelectedProgram(null);

      // Refresh to get updated counts
      fetchPrograms();

      alert('Interest expressed successfully! The sponsor will be notified.');
    } catch (err) {
      console.error('Error expressing interest:', err);
      alert(err instanceof Error ? err.message : 'Failed to express interest');
    } finally {
      setSubmitting(false);
    }
  };

  const getSponsorTypeBadgeColor = (type: string): string => {
    const colors: Record<string, string> = {
      'HEALTH_PLAN': 'bg-blue-100 text-blue-800',
      'MANAGED_CARE': 'bg-purple-100 text-purple-800',
      'STATE_AUTHORITY': 'bg-green-100 text-green-800',
      'STATE_ASSOCIATION': 'bg-teal-100 text-teal-800',
      'FEDERAL_AGENCY': 'bg-red-100 text-red-800',
      'ACO': 'bg-orange-100 text-orange-800',
      'HOSPITAL_SYSTEM': 'bg-indigo-100 text-indigo-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadgeColor = (status: string): string => {
    const colors: Record<string, string> = {
      'discovered': 'bg-purple-100 text-purple-800',
      'verified': 'bg-green-100 text-green-800',
      'converted': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return fmt.medium(dateString);
  };

  const formatCurrency = (amount: number | null): string => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getDaysUntilDeadline = (deadline: string | null): number | null => {
    if (!deadline) return null;
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
        <span className="ml-2 text-gray-500 dark:text-gray-400">{t('marketplace.loading_program_intelligence', 'Loading program intelligence...')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600">{error}</p>
        <Button onClick={fetchPrograms} className="mt-4">
          {t('marketplace.retry', 'Retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && (
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Lightbulb className="h-6 w-6 text-purple-600" />
              {t('marketplace.program_intelligence', 'Program Intelligence')}
            </h2>
          )}
          <p className="text-gray-500 mt-1">
            {t('marketplace.discover_upcoming_programs_from_health', 'Discover upcoming programs from health plans, state authorities, and associations')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="h-4 w-4 inline-block mr-1" />
              {t('marketplace.list', 'List')}
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'map'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Map className="h-4 w-4 inline-block mr-1" />
              {t('marketplace.map', 'Map')}
            </button>
          </div>
          <Badge variant="outline" className="text-purple-600 border-purple-300">
            {filteredPrograms.length} Programs Found
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketplace.filters', 'Filters')}</span>
        </div>

        <div className="space-y-4">
          {/* First row: Search, Sponsor Type, Program Type, State, Deadline */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('marketplace.search_programs', 'Search programs...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Sponsor Type */}
            <select
              value={sponsorTypeFilter}
              onChange={(e) => setSponsorTypeFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="">{t('marketplace.all_sponsor_types', 'All Sponsor Types')}</option>
              {sponsorTypes.map(type => (
                <option key={type.code} value={type.code}>{type.name}</option>
              ))}
            </select>

            {/* Program Type */}
            <select
              value={programTypeFilter}
              onChange={(e) => setProgramTypeFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="">{t('marketplace.all_program_types', 'All Program Types')}</option>
              {programTypes.map(type => (
                <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
              ))}
            </select>

            {/* State */}
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="">{t('marketplace.all_states', 'All States')}</option>
              <option value="CA">{t('marketplace.california', 'California')}</option>
              <option value="TX">{t('marketplace.texas', 'Texas')}</option>
              <option value="NY">{t('marketplace.new_york', 'New York')}</option>
              <option value="FL">{t('marketplace.florida', 'Florida')}</option>
              {/* Add more states as needed */}
            </select>

            {/* Deadline filter */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hasDeadlineOnly}
                onChange={(e) => setHasDeadlineOnly(e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">{t('marketplace.has_deadline', 'Has Deadline')}</span>
            </label>
          </div>

          {/* Second row: Location-based filtering */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t">
            <div className="md:col-span-2">
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Enter your location (e.g., Los Angeles, CA or 90001)"
                  value={userLocation}
                  onChange={(e) => setUserLocation(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                {userLocation && userLocation !== debouncedLocation && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              {debouncedLocation && (
                <p className="text-xs text-gray-500 mt-1">
                  Filtering by location: {debouncedLocation}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 whitespace-nowrap">{t('marketplace.within', 'Within')}</label>
              <select
                value={maxDistance}
                onChange={(e) => setMaxDistance(Number(e.target.value))}
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                disabled={!debouncedLocation}
              >
                <option value="10">10 miles</option>
                <option value="25">25 miles</option>
                <option value="50">50 miles</option>
                <option value="100">100 miles</option>
                <option value="250">250 miles</option>
              </select>
            </div>
          </div>
        </div>
      </Card>

      {/* Content: Map or List View */}
      {viewMode === 'map' ? (
        <ProgramMapView programs={filteredPrograms} userLocation={userLocation} />
      ) : filteredPrograms.length === 0 ? (
        <Card className="p-8 text-center">
          <Lightbulb className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">{t('marketplace.no_programs_found_matching_your', 'No programs found matching your criteria.')}</p>
          <p className="text-sm text-gray-400 mt-2">{t('marketplace.try_adjusting_your_filters_or', 'Try adjusting your filters or check back later for new programs.')}</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredPrograms.map(program => {
            const isExpanded = expandedCards.has(program.id);
            const daysUntilDeadline = getDaysUntilDeadline(program.application_deadline);

            return (
              <Card key={program.id} className="p-6 hover:shadow-lg transition-shadow">
                {/* Header Row */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {program.program_name}
                      </h3>
                      <Badge className={getStatusBadgeColor(program.intelligence_status)}>
                        {program.intelligence_status}
                      </Badge>

                      {/* Enrollment Growth Badge */}
                      {program.enrollment_trend_direction && program.enrollment_growth_rate_12mo !== null && (
                        <Badge className={
                          program.enrollment_trend_direction === 'growing'
                            ? 'bg-green-100 text-green-800 border-green-200'
                            : program.enrollment_trend_direction === 'declining'
                            ? 'bg-red-100 text-red-800 border-red-200'
                            : 'bg-gray-100 text-gray-800 border-gray-200'
                        }>
                          <TrendingUp className={`h-3 w-3 mr-1 ${
                            program.enrollment_trend_direction === 'declining' ? 'rotate-180' : ''
                          }`} />
                          {program.enrollment_trend_direction === 'growing' ? '+' : ''}
                          {Math.abs(program.enrollment_growth_rate_12mo).toFixed(1)}% 12mo
                        </Badge>
                      )}

                      {/* Distance Badge (if geo-filtered) */}
                      {program.distance_miles !== undefined && (
                        <Badge variant="outline" className="text-xs">
                          <MapPin className="h-3 w-3 mr-1" />
                          {program.distance_miles} mi away
                          {program.closest_county && ` • ${program.closest_county}`}
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-300">
                      <span className="flex items-center gap-1">
                        <Building2 className="h-4 w-4" />
                        {program.discovered_sponsor_name}
                      </span>
                      {program.discovered_sponsor_type && (
                        <Badge className={getSponsorTypeBadgeColor(program.discovered_sponsor_type)}>
                          {program.discovered_sponsor_type.replace(/_/g, ' ')}
                        </Badge>
                      )}
                      {program.sponsor_headquarters_state && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {program.sponsor_headquarters_state}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quick Interest Button & Deadline Badge */}
                  <div className="flex items-center gap-3">
                    {/* Quick Flag/Interest Icon with Count */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(program);
                        }}
                        className={`p-2 rounded-full transition-colors ${
                          favoritedPrograms.has(program.id)
                            ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                            : 'hover:bg-gray-100 text-gray-400 hover:text-blue-600'
                        }`}
                        title={favoritedPrograms.has(program.id) ? "Remove flag" : "Flag interest"}
                      >
                        <Flag className={`h-5 w-5 ${favoritedPrograms.has(program.id) ? 'fill-current' : ''}`} />
                      </button>
                      {/* Interest count - shows how many orgs have flagged */}
                      {((program.total_interested_cbos || 0) + (favoritedPrograms.has(program.id) ? 1 : 0)) > 0 && (
                        <span className="text-xs text-gray-500 font-medium">
                          {(program.total_interested_cbos || 0) + (favoritedPrograms.has(program.id) ? 1 : 0)}
                        </span>
                      )}
                    </div>

                    {/* Deadline Badge */}
                    {daysUntilDeadline !== null && (
                      <div className={`text-right ${daysUntilDeadline <= 7 ? 'text-red-600' : daysUntilDeadline <= 30 ? 'text-orange-600' : 'text-gray-600'}`}>
                        <div className="flex items-center gap-1 text-sm font-medium">
                          <Clock className="h-4 w-4" />
                          {daysUntilDeadline} days left
                        </div>
                        <div className="text-xs">
                          Deadline: {formatDate(program.application_deadline)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Key Metrics Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('marketplace.target_enrollment', 'Target Enrollment')}</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {program.target_enrollment?.toLocaleString() || 'TBD'}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('marketplace.per_member_value', 'Per Member Value')}</div>
                    <div className="text-lg font-semibold text-green-600">
                      {formatCurrency(program.estimated_per_member_value)}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('marketplace.interested_cbos', 'Interested CBOs')}</div>
                    <div className="text-lg font-semibold text-purple-600">
                      {program.total_interested_cbos || 0}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('marketplace.readiness_score', 'Readiness Score')}</div>
                    <div className="flex items-center gap-2">
                      <Progress
                        value={program.execution_readiness_score || 0}
                        className="h-2 flex-1"
                      />
                      <span className="text-sm font-medium">
                        {Math.round(program.execution_readiness_score || 0)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Services & Requirements Summary */}
                <div className="mb-4">
                  {program.service_categories && program.service_categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {program.service_categories.slice(0, 4).map((service, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {service.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                      {program.service_categories.length > 4 && (
                        <Badge variant="outline" className="text-xs">
                          +{program.service_categories.length - 4} more
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Provider Needs & Specialty Gaps Alert */}
                  {(program.number_of_providers_needed || program.priority_specialties_needed) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-amber-900 mb-1">
                            {t('marketplace.provider_network_opportunity', 'Provider Network Opportunity')}
                          </div>
                          {program.number_of_providers_needed && (
                            <div className="text-xs text-amber-700">
                              {t('marketplace.seeking', 'Seeking')} <strong>{program.number_of_providers_needed}</strong> providers
                            </div>
                          )}
                          {program.priority_specialties_needed && program.priority_specialties_needed.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {program.priority_specialties_needed.slice(0, 5).map((specialty, idx) => (
                                <span key={idx} className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                                  {specialty}
                                </span>
                              ))}
                              {program.priority_specialties_needed.length > 5 && (
                                <span className="text-xs text-amber-700">
                                  +{program.priority_specialties_needed.length - 5} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {program.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {program.description}
                    </p>
                  )}
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t pt-4 mt-4 space-y-4">
                    {/* Program Classification & Risk */}
                    {(program.program_type_code || program.risk_arrangement) && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {program.program_type_code && (
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{t('marketplace.program_type', 'Program Type')}</div>
                            <div className="text-sm font-medium">{program.program_type_code.replace(/_/g, ' ')}</div>
                          </div>
                        )}
                        {program.risk_arrangement && (
                          <div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <Shield className="h-3 w-3" />
                              {t('marketplace.risk_arrangement', 'Risk Arrangement')}
                            </div>
                            <Badge className={`text-xs ${program.risk_arrangement === 'two_sided' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                              {program.risk_arrangement.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Eligibility Criteria */}
                    {program.eligibility_criteria && Object.keys(program.eligibility_criteria).length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {t('marketplace.eligibility_criteria', 'Eligibility Criteria')}
                        </h4>
                        <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                          {program.eligibility_criteria.insurance_types && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 dark:text-gray-400">{t('marketplace.insurance', 'Insurance:')}</span>
                              <div className="flex flex-wrap gap-1">
                                {program.eligibility_criteria.insurance_types.map((type, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs bg-white dark:bg-gray-800">
                                    {type}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          {program.eligibility_criteria.age_min && (
                            <div className="text-xs">
                              <span className="text-gray-500 dark:text-gray-400">{t('marketplace.minimum_age', 'Minimum Age:')}</span>
                              <span className="ml-1 font-medium">{program.eligibility_criteria.age_min}+</span>
                            </div>
                          )}
                          {program.eligibility_criteria.attribution_method && (
                            <div className="text-xs">
                              <span className="text-gray-500 dark:text-gray-400">{t('marketplace.attribution', 'Attribution:')}</span>
                              <span className="ml-1 font-medium">{program.eligibility_criteria.attribution_method}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Covered Services */}
                    {program.covered_services && program.covered_services.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                          <Activity className="h-4 w-4" />
                          {t('marketplace.covered_services', 'Covered Services')}
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {program.covered_services.map((service, idx) => (
                            <Badge key={idx} className="text-xs bg-purple-100 text-purple-800">
                              {service}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Geographic Coverage */}
                    {program.service_states && program.service_states.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                          <Globe className="h-4 w-4" />
                          {t('marketplace.service_area', 'Service Area')}
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {program.service_states.map((state, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {state}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Quality & Financial Performance */}
                    {(program.quality_performance || program.financial_performance) && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {program.quality_performance?.quality_score && (
                          <div className="bg-green-50 rounded-lg p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400">{t('marketplace.quality_score', 'Quality Score')}</div>
                            <div className="text-lg font-semibold text-green-600">
                              {program.quality_performance.quality_score}%
                            </div>
                          </div>
                        )}
                        {program.financial_performance?.savings_rate && (
                          <div className="bg-blue-50 rounded-lg p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400">{t('marketplace.savings_rate', 'Savings Rate')}</div>
                            <div className="text-lg font-semibold text-blue-600">
                              {program.financial_performance.savings_rate}%
                            </div>
                          </div>
                        )}
                        {program.financial_performance?.generated_savings_losses && (
                          <div className="bg-yellow-50 rounded-lg p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400">{t('marketplace.generated_savings', 'Generated Savings')}</div>
                            <div className="text-sm font-semibold text-yellow-700">
                              {formatCurrency(program.financial_performance.generated_savings_losses)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Requirements */}
                    {program.required_certifications && program.required_certifications.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                          <CheckCircle2 className="h-4 w-4" />
                          {t('marketplace.required_certifications', 'Required Certifications')}
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {program.required_certifications.map((cert, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {cert}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Reimbursement Details */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {program.reimbursement_model && (
                        <div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{t('marketplace.reimbursement_model', 'Reimbursement Model')}</div>
                          <div className="text-sm font-medium">{program.reimbursement_model.replace(/_/g, ' ')}</div>
                        </div>
                      )}
                      {program.base_reimbursement_rate && (
                        <div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{t('marketplace.base_rate', 'Base Rate')}</div>
                          <div className="text-sm font-medium">{formatCurrency(program.base_reimbursement_rate)}</div>
                        </div>
                      )}
                      {program.total_program_budget && (
                        <div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{t('marketplace.total_budget', 'Total Budget')}</div>
                          <div className="text-sm font-medium">{formatCurrency(program.total_program_budget)}</div>
                        </div>
                      )}
                    </div>

                    {/* Timeline */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('marketplace.program_start', 'Program Start')}</div>
                        <div className="text-sm font-medium">{formatDate(program.program_start_date)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('marketplace.program_end', 'Program End')}</div>
                        <div className="text-sm font-medium">{formatDate(program.program_end_date)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('marketplace.data_source', 'Data Source')}</div>
                        {program.source_url ? (
                          <a
                            href={program.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                          >
                            {program.data_source || 'View Source'}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <div className="text-sm font-medium">{program.data_source || 'N/A'}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions Row */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <button
                    onClick={() => toggleCardExpansion(program.id)}
                    className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="h-4 w-4" />
                        {t('marketplace.show_less', 'Show Less')}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4" />
                        {t('marketplace.show_details', 'Show Details')}
                      </>
                    )}
                  </button>

                  <Button
                    onClick={() => handleExpressInterest(program)}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {t('marketplace.express_interest', 'Express Interest')}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Express Interest Modal */}
      <Dialog open={showInterestModal} onOpenChange={setShowInterestModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-purple-600" />
              {t('marketplace.express_interest_1', 'Express Interest')}
            </DialogTitle>
            <DialogDescription>
              Let {selectedProgram?.discovered_sponsor_name} know you're interested in {selectedProgram?.program_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Interest Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('marketplace.interest_level', 'Interest Level *')}
              </label>
              <select
                value={interestLevel}
                onChange={(e) => setInterestLevel(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="HIGH">{t('marketplace.high_ready_to_participate', 'High - Ready to participate')}</option>
                <option value="MEDIUM">{t('marketplace.medium_need_more_information', 'Medium - Need more information')}</option>
                <option value="EXPLORATORY">{t('marketplace.exploratory_learning_about_it', 'Exploratory - Learning about it')}</option>
              </select>
            </div>

            {/* Interest Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('marketplace.interest_type', 'Interest Type')}
              </label>
              <select
                value={interestType}
                onChange={(e) => setInterestType(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="PARTICIPATE">{t('marketplace.direct_participation', 'Direct Participation')}</option>
                <option value="PARTNER">{t('marketplace.partnership_with_other_cbos', 'Partnership with Other CBOs')}</option>
                <option value="SUBCONTRACT">{t('marketplace.subcontract_services', 'Subcontract Services')}</option>
                <option value="LEARN_MORE">{t('marketplace.learn_more_first', 'Learn More First')}</option>
              </select>
            </div>

            {/* Declared Capacity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('marketplace.members_you_can_serve', 'Members You Can Serve')}
              </label>
              <input
                type="number"
                value={declaredCapacity}
                onChange={(e) => setDeclaredCapacity(parseInt(e.target.value) || 0)}
                placeholder="e.g., 500"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('marketplace.estimated_number_of_members_your', 'Estimated number of members your organization can serve')}
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('marketplace.notes_or_questions', 'Notes or Questions')}
              </label>
              <textarea
                value={interestNotes}
                onChange={(e) => setInterestNotes(e.target.value)}
                placeholder={t('marketplace.share_your_capabilities_questions_or', 'Share your capabilities, questions, or comments...')}
                rows={3}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowInterestModal(false)}
            >
              {t('marketplace.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={submitInterest}
              disabled={submitting}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {submitting ? (
                <>
                  <LoadingSpinner className="h-4 w-4 mr-2" />
                  {t('marketplace.submitting', 'Submitting...')}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {t('marketplace.submit_interest', 'Submit Interest')}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramIntelligenceCatalog;
